import { Effect, Queue, Schema as S, Stream } from "effect";
import * as Command from "foldkit/command";
import { defineMessageUnion } from "foldkit/message";
import * as Mount from "foldkit/mount";

import type { LinkView } from "../api/schemas/notes";
import { i18next } from "../i18n";
import type { Handle } from "./noteEditorLivePreview";
import { headingLabel, linkLabel } from "./wikiLinks";

/**
 * The note page's one editing surface (U15): CodeMirror 6 with live-preview
 * decorations (`noteEditorLivePreview.ts`, lazy-loaded so CodeMirror stays
 * out of the route chunk). Mount args are frozen; afterwards the Model
 * reaches the editor only through `SetNoteText` / `SetNoteLinks`, which look
 * the editor up by id. Keystrokes come back as `NoteEditorChanged`;
 * Mod-click or Mod-Enter on a `[[link]]` as `NoteEditorActivatedLink`;
 * Mod-S as `NoteEditorRequestedSave`.
 */

export const EditorLink = S.Struct({
	raw: S.String,
	status: S.Literals(["resolved", "ambiguous", "broken"]),
	label: S.String,
	title: S.String,
});
export type EditorLink = typeof EditorLink.Type;

/** The server's links as the editor shows them: label and an open hint. */
export const editorLinks = (
	links: ReadonlyArray<LinkView>,
): ReadonlyArray<EditorLink> =>
	links.map((link) => {
		const label = `${linkLabel(link)}${headingLabel(link)}`;
		const title =
			link.status === "resolved"
				? i18next.t("knowledge.note.linkHint.resolved", { name: link.name })
				: link.status === "ambiguous"
					? i18next.t("knowledge.note.linkHint.ambiguous", {
							count: link.ids.length,
						})
					: i18next.t("knowledge.note.linkHint.broken", {
							target: link.target,
						});
		return { raw: link.raw, status: link.status, label, title };
	});

const handles = new Map<string, Handle>();

export const Message = defineMessageUnion({
	NoteEditorChanged: { id: S.String, text: S.String },
	NoteEditorActivatedLink: { id: S.String, raw: S.String },
	NoteEditorRequestedSave: { id: S.String },
});
export type Message = typeof Message.Type;

export const CommandMessage = defineMessageUnion({
	CompletedNoteEditorCommand: {},
});

/** Model→editor: replaces the document (discard, reload). */
export const SetNoteText = Command.define("SetNoteText", {
	args: { id: S.String, text: S.String },
	messages: [CommandMessage.CompletedNoteEditorCommand],
	execute: ({ id, text }) =>
		Effect.sync(() => {
			handles.get(id)?.setText(text);
			return CommandMessage.CompletedNoteEditorCommand();
		}),
});

/** Model→editor: the server's fresh link resolution after a save. */
export const SetNoteLinks = Command.define("SetNoteLinks", {
	args: { id: S.String, links: S.Array(EditorLink) },
	messages: [CommandMessage.CompletedNoteEditorCommand],
	execute: ({ id, links }) =>
		Effect.sync(() => {
			handles.get(id)?.setLinks(links);
			return CommandMessage.CompletedNoteEditorCommand();
		}),
});

export const NoteEditor = Mount.defineStream("NoteEditor", {
	args: {
		id: S.String,
		initialText: S.String,
		title: S.String,
		tags: S.Array(S.String),
		links: S.Array(EditorLink),
		readOnly: S.Boolean,
		label: S.String,
		placeholder: S.String,
		describedBy: S.String,
	},
	messages: [
		Message.NoteEditorChanged,
		Message.NoteEditorActivatedLink,
		Message.NoteEditorRequestedSave,
	],
	execute: ({ element, id, initialText, ...rest }) =>
		Stream.callback<Message>((queue) =>
			Effect.gen(function* () {
				yield* Effect.acquireRelease(
					Effect.promise(async () => {
						const { mount } = await import("./noteEditorLivePreview");
						const emit = (message: Message) => {
							Queue.offerUnsafe(queue, message);
						};
						const handle = mount(element, {
							...rest,
							doc: initialText,
							onChange: (text) => emit(Message.NoteEditorChanged({ id, text })),
							onActivate: (raw) =>
								emit(Message.NoteEditorActivatedLink({ id, raw })),
							onSave: () => emit(Message.NoteEditorRequestedSave({ id })),
						});
						handles.set(id, handle);
						return handle;
					}),
					(handle) =>
						Effect.sync(() => {
							// Release is async; a remount under the same id may already own the slot.
							if (handles.get(id) === handle) handles.delete(id);
							handle.view.destroy();
						}),
				);
				return yield* Effect.never;
			}),
		),
});
